import { useState } from "react";
import { Download, Loader2, ShieldAlert, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { generateClinicianBrief } from "@/lib/generateClinicianBrief";

const ClinicianBriefDownload = () => {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");

  const handleDownload = async () => {
    if (status === "loading") return;
    setStatus("loading");
    try {
      await generateClinicianBrief();
      setStatus("success");
    } catch (error) {
      console.error("clinician information brief generation failed", error);
      setStatus("error");
    }
  };

  return (
    <section
      aria-labelledby="clinician-brief-heading"
      className="max-w-2xl mx-auto rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/8 to-primary/3 p-6 sm:p-8"
    >
      <div className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-primary font-medium mb-3">
        <ShieldAlert className="h-4 w-4" aria-hidden="true" />
        Information only · PDF · generated locally
      </div>
      <h3 id="clinician-brief-heading" className="font-display text-2xl sm:text-3xl text-foreground tracking-tight leading-tight">
        Clinician information and release-control brief
      </h3>
      <p className="mt-3 text-[15px] text-foreground/90 leading-relaxed">
        This two-page brief explains the current public hold, the retrospective wellness boundary, and the evidence required before any clinician program, patient-data workflow, or pilot offer can open.
      </p>

      <div className="mt-5 rounded-xl border border-border/70 bg-background/70 p-4 text-sm leading-relaxed text-foreground/85">
        No name, email, organization, credential, license, patient information, or health information is requested or transmitted. The PDF is built in this browser and downloaded directly to your device.
      </div>

      {status === "success" && (
        <div role="status" className="mt-5 flex items-start gap-3 rounded-xl border border-primary/20 bg-primary/5 p-4 text-sm text-foreground">
          <CheckCircle2 className="mt-0.5 h-5 w-5 flex-none text-primary" aria-hidden="true" />
          <p>The information brief was generated locally. Nothing was submitted or stored.</p>
        </div>
      )}

      {status === "error" && (
        <div role="alert" className="mt-5 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-foreground">
          The PDF could not be generated in this browser. No information was submitted or stored.
        </div>
      )}

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button
          type="button"
          variant="premium"
          size="lg"
          onClick={handleDownload}
          disabled={status === "loading"}
          aria-busy={status === "loading"}
          className="w-full sm:w-auto"
        >
          {status === "loading" ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              Generating local PDF…
            </>
          ) : (
            <>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Download the information brief
            </>
          )}
        </Button>

        <a
          href="tel:+18887212444"
          aria-label="Call 1-888-721-2444"
          className="inline-flex min-h-11 items-center justify-center rounded-md px-3 text-sm text-foreground underline underline-offset-4 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          Call 1-888-721-2444
        </a>
      </div>

      <p className="mt-4 text-xs leading-relaxed text-foreground/80">
        Use public contact channels only for general, non-sensitive business questions. Do not send patient, health, medical, credential, license, or other sensitive information.
      </p>
    </section>
  );
};

export default ClinicianBriefDownload;
